// FlowsProvider — context replacement for the design's window.ProPropFlows
// global. Wraps the agent shell so any descendant can call useFlows() and
// open one of the eight flow modals. The provider mounts every modal once
// at app root; each one keeps its own open/ctx state in this component.

import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { AgentCase } from "@/lib/agentMockData";
import type { Bid } from "@/lib/proagentApi";
import { AddBuyerLeadModal } from "./AddBuyerLeadModal";
import { AddPropertyModal } from "./AddPropertyModal";
import { LogOfferModal } from "./LogOfferModal";
import { NewCaseWizard } from "./NewCaseWizard";
import { OfferDetailDrawer } from "./OfferDetailDrawer";
import { SaleTermsModal } from "./SaleTermsModal";
import { ScheduleViewingModal } from "./ScheduleViewingModal";
import { SendReportModal } from "./SendReportModal";
import { ViewingDetailDrawer, type ViewingRecord } from "./ViewingDetailDrawer";

export type ScheduleViewingCtx = {
  caseObj?: AgentCase;
  prefill?: Record<string, string>;
};
export type LogOfferCtx = { caseObj?: AgentCase; prefill?: Record<string, string> };
export type SendReportCtx = {
  caseObj?: AgentCase;
  audience?: "client" | "seller";
  recipientName?: string;
  recipients?: string;
  subject?: string;
  message?: string;
  // shareLink is the public report URL minted by ShareCard.
  // When present, SendReportModal pre-pends it to the personal-note
  // body so the agent's copy/paste already contains the link.
  shareLink?: string;
};
export type AddBuyerLeadCtx = { caseObj?: AgentCase };
export type AddPropertyCtx = { caseObj?: AgentCase };
export type SaleTermsCtx = { caseObj?: AgentCase };

export type FlowsApi = {
  openNewCase: () => void;
  openScheduleViewing: (ctx?: ScheduleViewingCtx) => void;
  openViewingDetail: (record: ViewingRecord, opts?: { onSaved?: () => void }) => void;
  openLogOffer: (ctx?: LogOfferCtx) => void;
  openOfferDetail: (bid: Bid, opts?: { onSaved?: () => void }) => void;
  openSaleTerms: (ctx: SaleTermsCtx) => void;
  openSendReport: (ctx: SendReportCtx) => void;
  openAddBuyerLead: (ctx?: AddBuyerLeadCtx) => void;
  openAddProperty: (ctx?: AddPropertyCtx, opts?: { onSaved?: () => void }) => void;
};

const FlowsContext = createContext<FlowsApi | null>(null);

export function useFlows(): FlowsApi {
  const ctx = useContext(FlowsContext);
  if (!ctx) {
    throw new Error("useFlows must be used inside <FlowsProvider>");
  }
  return ctx;
}

type Slot<T> = { open: boolean; ctx: T | null };

export function FlowsProvider({ children }: { children: ReactNode }) {
  const [newCase, setNewCase] = useState<{ open: boolean }>({ open: false });
  const [scheduleViewing, setScheduleViewing] = useState<Slot<ScheduleViewingCtx>>({ open: false, ctx: null });
  const [viewingDetail, setViewingDetail] = useState<{
    open: boolean;
    record: ViewingRecord | null;
    onSaved?: () => void;
  }>({ open: false, record: null });
  const [logOffer, setLogOffer] = useState<Slot<LogOfferCtx>>({ open: false, ctx: null });
  const [offerDetail, setOfferDetail] = useState<{
    open: boolean;
    bid: Bid | null;
    onSaved?: () => void;
  }>({ open: false, bid: null });
  const [saleTerms, setSaleTerms] = useState<Slot<SaleTermsCtx>>({ open: false, ctx: null });
  const [sendReport, setSendReport] = useState<Slot<SendReportCtx>>({ open: false, ctx: null });
  const [addBuyerLead, setAddBuyerLead] = useState<Slot<AddBuyerLeadCtx>>({ open: false, ctx: null });
  const [addProperty, setAddProperty] = useState<
    Slot<AddPropertyCtx> & { onSaved?: () => void }
  >({ open: false, ctx: null });

  const openNewCase = useCallback(() => setNewCase({ open: true }), []);
  const openScheduleViewing = useCallback(
    (ctx?: ScheduleViewingCtx) => setScheduleViewing({ open: true, ctx: ctx ?? null }),
    [],
  );
  const openViewingDetail = useCallback(
    (record: ViewingRecord, opts?: { onSaved?: () => void }) =>
      setViewingDetail({ open: true, record, onSaved: opts?.onSaved }),
    [],
  );
  const openLogOffer = useCallback(
    (ctx?: LogOfferCtx) => setLogOffer({ open: true, ctx: ctx ?? null }),
    [],
  );
  const openOfferDetail = useCallback(
    (bid: Bid, opts?: { onSaved?: () => void }) =>
      setOfferDetail({ open: true, bid, onSaved: opts?.onSaved }),
    [],
  );
  const openSaleTerms = useCallback(
    (ctx: SaleTermsCtx) => setSaleTerms({ open: true, ctx }),
    [],
  );
  const openSendReport = useCallback(
    (ctx: SendReportCtx) => setSendReport({ open: true, ctx }),
    [],
  );
  const openAddBuyerLead = useCallback(
    (ctx?: AddBuyerLeadCtx) => setAddBuyerLead({ open: true, ctx: ctx ?? null }),
    [],
  );
  const openAddProperty = useCallback(
    (ctx?: AddPropertyCtx, opts?: { onSaved?: () => void }) =>
      setAddProperty({ open: true, ctx: ctx ?? null, onSaved: opts?.onSaved }),
    [],
  );

  const api = useMemo<FlowsApi>(
    () => ({
      openNewCase,
      openScheduleViewing,
      openViewingDetail,
      openLogOffer,
      openOfferDetail,
      openSaleTerms,
      openSendReport,
      openAddBuyerLead,
      openAddProperty,
    }),
    [
      openNewCase,
      openScheduleViewing,
      openViewingDetail,
      openLogOffer,
      openOfferDetail,
      openSaleTerms,
      openSendReport,
      openAddBuyerLead,
      openAddProperty,
    ],
  );

  return (
    <FlowsContext.Provider value={api}>
      {children}
      <NewCaseWizard
        open={newCase.open}
        onClose={() => setNewCase({ open: false })}
      />
      <ScheduleViewingModal
        open={scheduleViewing.open}
        ctx={scheduleViewing.ctx}
        onClose={() => setScheduleViewing({ open: false, ctx: null })}
      />
      <ViewingDetailDrawer
        open={viewingDetail.open}
        record={viewingDetail.record}
        onSaved={viewingDetail.onSaved}
        onClose={() => setViewingDetail({ open: false, record: null })}
      />
      <LogOfferModal
        open={logOffer.open}
        ctx={logOffer.ctx}
        onClose={() => setLogOffer({ open: false, ctx: null })}
      />
      <OfferDetailDrawer
        open={offerDetail.open}
        bid={offerDetail.bid}
        onSaved={offerDetail.onSaved}
        onClose={() => setOfferDetail({ open: false, bid: null })}
      />
      <SaleTermsModal
        open={saleTerms.open}
        ctx={saleTerms.ctx}
        onClose={() => setSaleTerms({ open: false, ctx: null })}
      />
      <SendReportModal
        open={sendReport.open}
        ctx={sendReport.ctx}
        onClose={() => setSendReport({ open: false, ctx: null })}
      />
      <AddBuyerLeadModal
        open={addBuyerLead.open}
        ctx={addBuyerLead.ctx}
        onClose={() => setAddBuyerLead({ open: false, ctx: null })}
      />
      <AddPropertyModal
        open={addProperty.open}
        ctx={addProperty.ctx}
        onSaved={addProperty.onSaved}
        onClose={() => setAddProperty({ open: false, ctx: null })}
      />
    </FlowsContext.Provider>
  );
}
